import React, { useState } from "react";

import {
  Button,
  Checkbox,
  Col,
  Flex,
  Form,
  Image,
  Input,
  Modal,
  Row,
  Space,
  Typography,
} from "antd";
import CARDS from "assets/payments/openpay/cards1.png";
import CARDS2 from "assets/payments/openpay/cards2.png";
import OPENPAY from "assets/payments/openpay/openpay.png";
import OPENPAY_SECURITY from "assets/payments/openpay/security.png";
import { toast } from "utils/toast";

import CreditCard from "js/components/Memberships/CreditCard";
import {
  AddCardModalProps,
  CreditCardItem,
} from "js/components/Memberships/types";
import { formatCardNumber } from "js/components/Payments/cardUtils";

import { useCreateCard } from "../../../hooks/usePayments";

const { Title } = Typography;

const AddCardModal: React.FC<AddCardModalProps> = ({
  isModalOpen,
  setIsModalOpen,
}) => {
  const [cardNumber, setCardNumber] = useState("");
  const [cvv, setCvv] = useState("");
  const [expirationMonth, setExpirationMonth] = useState("");
  const [expirationYear, setExpirationYear] = useState("");
  const [cardName, setCardName] = useState("");
  const [focusName, setFocusName] = useState("");

  const [form] = Form.useForm();
  const createCardMutation = useCreateCard();

  const openPay = window.OpenPay;

  openPay.setId(import.meta.env.VITE_APP_OPENPAY_MERCHANT_ID);
  openPay.setApiKey(import.meta.env.VITE_APP_OPENPAY_PUBLIC_API_KEY);
  const isSandbox = import.meta.env.VITE_APP_ENV == "development";
  openPay.setSandboxMode(isSandbox);

  const deviceSessionId = openPay.deviceData.setup(
    "payment-form",
    "deviceIdHiddenFieldName",
  );

  const onFinish = (values: any) => {
    openPay.token.create(
      {
        card_number: values.card_number.replace(/\s/g, ""),
        holder_name: values.holder_name,
        expiration_year: values.expiration_year,
        expiration_month: values.expiration_month,
        cvv2: values.cvv2,
      },
      (response: any) => {
        const tokenId = response.data.id;
        const last4 = response.data.card.card_number.slice(-4);
        const brand = response.data.card.brand;

        const newCard: CreditCardItem = {
          token: tokenId,
          last4: last4,
          brand: brand,
          is_default: values.isDefault ?? false,
          sessionId: deviceSessionId,
        };

        createCardMutation.mutate(newCard, {
          onSuccess: () => {
            setIsModalOpen(false);
            form.resetFields();
          },
        });
      },
      (response: any) => {
        const error =
          response.data.description !== undefined
            ? response.data.description
            : response.message;
        toast.error(`Error: ${error}`);
      },
    );
  };

  return (
    <Modal
      title="Agregar tarjeta de crédito o débito"
      open={isModalOpen}
      onCancel={() => {
        setIsModalOpen(false);
        form.resetFields();
      }}
      footer={null}
    >
      <Form
        id="payment-form"
        form={form}
        onFinish={onFinish}
        layout={"vertical"}
        disabled={createCardMutation.isPending}
      >
        <Flex justify={"center"} align={"center"} vertical>
          <Row style={{ marginTop: 10 }}>
            <Title level={5}>Tarjetas de Crédito</Title>
          </Row>
          <Row>
            <Col span={24}>
              <Image src={CARDS} />
            </Col>
          </Row>
          <Row style={{ marginTop: 20 }}>
            <Title level={5}>Tarjetas de Crédito</Title>
          </Row>
          <Row>
            <Col span={24}>
              <Image src={CARDS2} />
            </Col>
          </Row>
        </Flex>
        <Row style={{ marginTop: 30, marginBottom: 30 }} justify={"center"}>
          <CreditCard
            number={cardNumber}
            cvc={cvv}
            expiry={`${expirationMonth}${expirationYear}`}
            name={cardName}
            focus={focusName}
          />
        </Row>
        <Row gutter={30}>
          <Col span={12}>
            <Form.Item
              label="Nombre del titular"
              name="holder_name"
              data-openpay-card="holder_name"
              rules={[
                {
                  required: true,
                  message: "Por favor, ingresa el nombre del titular",
                },
              ]}
            >
              <Input
                placeholder="Como aparece en la tarjeta"
                autoComplete="off"
                onChange={(e: any) => setCardName(e.target.value)}
                onFocus={() => setFocusName("name")}
                data-sentry-ignore
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Número de tarjeta"
              name="card_number"
              data-openpay-card="card_number"
              getValueFromEvent={(e: any) => formatCardNumber(e.target.value)}
              rules={[
                {
                  required: true,
                  message: "Por favor, ingresa el número de la tarjeta",
                },
                {
                  validator: (_: any, value: string) => {
                    const digits = (value ?? "").replace(/\D/g, "");
                    if (!digits) return Promise.resolve();
                    if (digits.length < 15 || digits.length > 16) {
                      return Promise.reject(
                        new Error(
                          "El número de tarjeta debe tener 15 o 16 dígitos",
                        ),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                placeholder="1234 1234 1234 1234"
                maxLength={19}
                autoComplete="off"
                onChange={(e: any) =>
                  setCardNumber(formatCardNumber(e.target.value))
                }
                onFocus={() => setFocusName("number")}
                data-sentry-ignore
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={30}>
          <Col span={12}>
            <Form.Item
              label="Mes de expiración"
              name="expiration_month"
              data-openpay-card="expiration_month"
              rules={[
                {
                  required: true,
                  message: "Por favor, ingresa el mes de expiración",
                },
                {
                  max: 2,
                  min: 2,
                  message: "Selecciona un mes válido (01-12)",
                },
              ]}
            >
              <Input
                maxLength={2}
                minLength={2}
                placeholder="08"
                onChange={(e: any) =>
                  setExpirationMonth(e.target.value.toString())
                }
                onFocus={() => setFocusName("expiry")}
                autoComplete="off"
                data-sentry-ignore
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Año de expiración"
              name="expiration_year"
              data-openpay-card="expiration_year"
              rules={[
                {
                  required: true,
                  message: "Por favor, ingresa el año de expiración",
                },
                {
                  max: 2,
                  min: 2,
                  message: "Selecciona un año de expiración válido",
                },
              ]}
            >
              <Input
                placeholder="26"
                maxLength={2}
                minLength={2}
                onChange={(e: any) => setExpirationYear(e.target.value)}
                onFocus={() => setFocusName("expiry")}
                autoComplete="off"
                data-sentry-ignore
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item
              label="Código de seguridad (Cvv)"
              name="cvv2"
              data-openpay-card="cvv2"
              rules={[
                {
                  required: true,
                  message: "Por favor, ingresa el código de seguridad (Cvv)",
                },
                { max: 4, message: "El CVV es demasiado largo" },
                { min: 3, message: "El CVV es demasiado corto" },
              ]}
            >
              <Input
                maxLength={4}
                minLength={3}
                placeholder="123"
                autoComplete="off"
                onChange={(e: any) => setCvv(e.target.value)}
                onFocus={() => setFocusName("cvc")}
                data-sentry-ignore
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Space size={30}>
            <div>Transacciones realizadas vía:</div>
            <Image src={OPENPAY} alt="" />
            <Image src={OPENPAY_SECURITY} />
          </Space>
        </Row>
        <Row>
          <Form.Item name="isDefault" valuePropName="checked">
            <Checkbox id="isDefault">Hacer tarjeta predefinida</Checkbox>
          </Form.Item>
        </Row>
        <Row>
          <Form.Item>
            <Button
              htmlType={"submit"}
              type="primary"
              loading={createCardMutation.isPending}
            >
              {createCardMutation.isPending ? "Agregando..." : "Agregar"}
            </Button>
          </Form.Item>
        </Row>
      </Form>
    </Modal>
  );
};

export default AddCardModal;
